"use client";

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig, API_ORDER_EXPORT_ROUTER } from "../../../services/api-config";
import CookieUtils from "../../../utils/cookie-utils";
import { showToast } from "../../../components/custom/custom-toast";

/**
 * Nút "Xuất Excel" của danh sách đơn — xuất NỀN theo đúng bộ lọc đang chọn.
 *
 * Luồng: POST tạo job (BE trả `job_id` ngay) → hỏi tiến độ mỗi 2 giây → xong thì tự tải file.
 * Không gọi một request dài: gateway cắt request sau 30 giây, xuất cả tháng có thể lâu hơn.
 * Đóng modal trong lúc chạy vẫn được — BE vẫn làm tiếp, mở lại sẽ hỏi tiếp job cũ.
 * BE: order service `OrderExportService`.
 */

export interface OrderExportFilters {
  from_date?: string;
  to_date?: string;
  key_search?: string;
  /** Chuỗi: BE dùng chung GetOrderLaundryDto (branch_id @IsString) nên gửi số trong JSON body sẽ bị 400. */
  branch_id?: string;
  statuses?: number[];
  assigned_employee_id?: number;
  order_type?: number;
  requires_counting?: number;
  scope?: string;
}

interface ExportStatus {
  job_id: string;
  state: "waiting" | "active" | "completed" | "failed" | "expired" | string;
  progress: number;
  file_name: string;
  result: { total_orders: number; total_rows: number; size: number; expires_at: string } | null;
  error: string | null;
}

interface Props {
  filters: OrderExportFilters;
}

const POLL_MS = 2000;

/** "YYYY-MM-DD" → "DD-MM-YYYY": BE dùng chung DTO lọc của danh sách đơn (@IsTimeFormat("DD-MM-YYYY")). */
const toDMY = (ymd?: string) => {
  if (!ymd) return undefined;
  const [y, m, d] = ymd.split("-");
  return y && m && d ? `${d}-${m}-${y}` : ymd;
};

const OrderExportButton: React.FC<Props> = ({ filters }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<ExportStatus | null>(null);
  const [starting, setStarting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const downloadedJob = useRef<string | null>(null);

  const { request: createJob } = useFetchData.Post<ExportStatus>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });
  const { request: getStatus } = useFetchData.Get<ExportStatus>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });

  const stopPolling = () => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    pollTimer.current = null;
  };
  useEffect(() => stopPolling, []);

  const download = async (s: ExportStatus) => {
    setDownloading(true);
    try {
      const token = CookieUtils.getCurrentUser().access_token ?? "";
      const res = await axios.get(`${ApiConfig.API_URL}/api/${API_ORDER_EXPORT_ROUTER.API_DOWNLOAD(s.job_id)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "x-svc-id": ApiConfig.PROJECT_ID.ORDER_SERVICE,
          platform: "1",
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = s.file_name || "don-hang.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
      downloadedJob.current = s.job_id;
    } catch (e: any) {
      const gone = e?.response?.status === 410;
      showToast({ message: gone ? "File đã hết hạn, vui lòng xuất lại" : "Không tải được file", type: "error" });
      if (gone) setStatus({ ...s, state: "expired" });
    } finally {
      setDownloading(false);
    }
  };

  const poll = async (jobId: string) => {
    const res: any = await getStatus({ endPoint: API_ORDER_EXPORT_ROUTER.API_STATUS(jobId) });
    if (res?.status !== 200 || !res.data) {
      // Lỗi mạng tạm thời → thử lại, không bỏ job.
      pollTimer.current = setTimeout(() => poll(jobId), POLL_MS * 2);
      return;
    }
    const s: ExportStatus = res.data;
    setStatus(s);
    if (s.state === "waiting" || s.state === "active" || s.state === "delayed") {
      pollTimer.current = setTimeout(() => poll(jobId), POLL_MS);
      return;
    }
    if (s.state === "completed" && downloadedJob.current !== s.job_id) {
      await download(s);
    }
  };

  const start = async () => {
    stopPolling();
    setStarting(true);
    setStatus(null);
    downloadedJob.current = null;
    try {
      const res: any = await createJob({
        endPoint: API_ORDER_EXPORT_ROUTER.API_CREATE,
        data: { ...filters, from_date: toDMY(filters.from_date), to_date: toDMY(filters.to_date) },
      });
      if (res?.status !== 200 || !res.data) {
        showToast({ message: res?.message || "Không tạo được yêu cầu xuất file", type: "error" });
        return;
      }
      setStatus(res.data);
      await poll(res.data.job_id);
    } finally {
      setStarting(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    // Đang có job dở → hỏi tiếp; chưa có (hoặc lỗi / hết hạn) → tạo mới theo bộ lọc hiện tại.
    if (status && ["waiting", "active", "delayed"].includes(status.state)) {
      stopPolling();
      poll(status.job_id);
    } else if (status?.state === "completed") {
      // Đã có file của lần trước → chỉ mở để tải lại; muốn file theo bộ lọc mới thì bấm "Xuất lại".
    } else {
      start();
    }
  };

  const running = starting || (!!status && ["waiting", "active", "delayed"].includes(status.state));
  const progress = Math.max(0, Math.min(100, Number(status?.progress) || 0));

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-green-600 text-green-700 bg-white hover:bg-green-50 transition"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
        </svg>
        {running ? `Đang xuất ${progress}%` : "Xuất Excel"}
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-[440px] max-w-[95vw] p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Xuất Excel danh sách đơn</h3>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-700 text-lg" title="Đóng">
                ✕
              </button>
            </div>

            {running && (
              <div className="space-y-2">
                <p className="text-sm text-slate-600">
                  {status?.state === "waiting" ? "Đang chờ tới lượt xử lý…" : "Đang tạo file…"}
                </p>
                <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-green-600 transition-all" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-xs text-slate-500">
                  {progress}% — có thể đóng cửa sổ này, file vẫn được tạo; bấm lại "Xuất Excel" để xem tiếp.
                </p>
              </div>
            )}

            {!running && status?.state === "completed" && (
              <div className="space-y-2 text-sm text-slate-700">
                <p className="font-medium text-green-700">Đã xuất xong.</p>
                {status.result && (
                  <p>
                    {status.result.total_orders.toLocaleString("vi-VN")} đơn ·{" "}
                    {status.result.total_rows.toLocaleString("vi-VN")} dòng
                  </p>
                )}
                <p className="text-xs text-slate-500">File được giữ 2 giờ, sau đó cần xuất lại.</p>
              </div>
            )}

            {!running && status?.state === "failed" && (
              <p className="text-sm text-red-600">{status.error || "Xuất file thất bại"}</p>
            )}

            {!running && status?.state === "expired" && (
              <p className="text-sm text-amber-700">File đã hết hạn, bấm "Xuất lại" để tạo file mới.</p>
            )}

            <div className="mt-6 flex justify-end gap-3">
              {!running && status?.state === "completed" && (
                <button
                  onClick={() => download(status)}
                  disabled={downloading}
                  className="px-4 py-2 border rounded-lg hover:bg-slate-50 disabled:opacity-60"
                >
                  {downloading ? "Đang tải..." : "Tải lại file"}
                </button>
              )}
              {!running && (
                <button
                  onClick={start}
                  className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Xuất lại
                </button>
              )}
              <button onClick={() => setOpen(false)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderExportButton;
